import { StudentAssignmentItemWithAttempt } from '../../../../constants/types/lms/student/homework/type';
import TextWinds from '../../../common/TextWinds';
import { Button } from 'antd';
import { formatDueDateText } from '../../../../api/utils/util-func';
import {
  openTodoView,
  resolveJebsplayerUrl,
} from '../../../../util/openContentsWindow';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAppDispatch } from '../../../../store/hook';
import { openAlert, openConfirm } from '../../../../store/features/modalSlice';
import { patchStudentAssignmentStatus } from '../../../../api/utils/lms/student/homwork/api';
import {
  SUBJECT_COLOR_MAP,
  SUBJECT_LABEL_MAP,
} from '../../../../constants/types/lms/type';

interface StudentAssignmentCardProps {
  assignment: StudentAssignmentItemWithAttempt;
  variant: 'urgent' | 'normal';
  fetchAssignmentInfo: () => Promise<void>;
}

const getAssignmentActionMeta = (
  assignment: StudentAssignmentItemWithAttempt,
) => {
  if (assignment.status === 'COMPLETED') {
    const attemptCount = assignment.attemptCount ?? 0;
    const attemptLimit = assignment.attemptLimit ?? 0;

    const canRetry = attemptCount < attemptLimit;

    return {
      label: canRetry ? `다시하기 (${attemptCount}/${attemptLimit}회)` : '완료',
      isVisible: canRetry,
    };
  }

  if (assignment.status === 'NOT_STARTED') {
    return {
      label: '시작하기',
      isVisible: true,
    };
  }

  if (assignment.status === 'IN_PROGRESS') {
    return {
      label: '진행 중',
      isVisible: true,
    };
  }

  return {
    label: '오류',
    isVisible: false,
  };
};

/**
 * 개편 LMS (학생) - 클래스 상세 페이지 > 콘텐츠 판넬 > 우측 [할 일 목록] > 젭스온(AI인증제 제외), WMW, HFHJ 카드
 */
const StudentAssignmentCard = ({
  assignment,
  variant,
  fetchAssignmentInfo,
}: StudentAssignmentCardProps) => {
  const { classId } = useParams();

  const dispatch = useAppDispatch();

  const setTodoView = useState<Window>(undefined)[1];

  const onCloseNewWindow = () => {
    fetchAssignmentInfo();
  };

  const handleAssignmentEnterClick = async () => {
    dispatch(
      openConfirm({
        message: (
          <div className="flex flex-col items-center text-center md:my-3">
            {assignment.attemptCount != null &&
              assignment.attemptLimit != null && (
                <div className="text-base font-bold text-gray9">
                  {`남은 응시 횟수 ${assignment.attemptLimit - assignment.attemptCount}회`}
                </div>
              )}

            <div className="mt-1 text-sm font-medium text-gray7">
              계속 도전해 볼까요?
            </div>
          </div>
        ),
        confirmText: '계속하기',
        cancelText: '닫기',
        onConfirm: async () => {
          if (!assignment.link) {
            dispatch(
              openAlert({
                message: '과제 링크가 없습니다. 관리자에게 문의해주세요.',
              }),
            );
            return;
          }

          openTodoView(
            { url: resolveJebsplayerUrl(assignment.link) },
            setTodoView,
            onCloseNewWindow,
          );

          if (assignment.status !== 'COMPLETED') {
            await patchStudentAssignmentStatus(
              classId,
              assignment.todoId,
              'IN_PROGRESS',
            );
          }

          await fetchAssignmentInfo();
        },
      }),
    );
  };

  const isUrgent = variant === 'urgent';

  const assignmentActionMeta = getAssignmentActionMeta(assignment);

  return (
    <article
      className={[
        'overflow-hidden rounded-xl border border-gray3 bg-white shadow-sm',
        isUrgent
          ? 'border-l-4 border-l-red-500'
          : 'border-l-4 border-l-[#0B8ED0]',
      ].join(' ')}
    >
      <div className="flex items-center justify-between gap-3 px-4 py-4">
        <div className="flex min-w-0 flex-1 items-center gap-3 xl:gap-4">
          <div
            className={[
              'flex h-12 w-12 shrink-0 items-center justify-center whitespace-pre-line rounded-xl text-center font-extrabold leading-tight',
              SUBJECT_COLOR_MAP[assignment.type],
            ].join(' ')}
          >
            {SUBJECT_LABEL_MAP[assignment.type]}
          </div>

          <div className="min-w-0 flex-1">
            <TextWinds
              type="content_body2"
              color="gray9"
              className="break-keep text-sm font-bold leading-snug xl:line-clamp-1 xl:text-base"
            >
              {`${assignment.book} · ${assignment.title}`}
            </TextWinds>

            <TextWinds
              type="content_body3"
              color="gray7"
              className="mt-1 block text-xs xl:text-sm"
            >
              {formatDueDateText(assignment.dueDate)}
            </TextWinds>
          </div>
        </div>

        {assignmentActionMeta.isVisible && (
          <div className="flex shrink-0 flex-col items-center gap-1">
            <Button
              type="primary"
              className="rounded-lg !border-purple5 !bg-purple5 px-4 hover:!border-purple6 hover:!bg-purple6"
              onClick={handleAssignmentEnterClick}
            >
              {assignmentActionMeta.label}
            </Button>
          </div>
        )}
      </div>
    </article>
  );
};

export default StudentAssignmentCard;
